import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Post } from "@/lib/types";
import { PostCard } from "./PostCard";
import { Skeleton } from "../ui/skeleton";
import { useToast } from "@/hooks/use-toast";

interface PostsContainerProps {
  category?: string;
  queryKey: string[];
  orderBy?: { column: string; ascending: boolean };
  limit?: number;
  reportedOnly?: boolean;
  excludeGroupPosts?: boolean;
  friendsOnly?: string[];
  showSuggested?: boolean;
}

export const PostsContainer = ({ 
  category, 
  queryKey, 
  orderBy, 
  limit, 
  reportedOnly,
  excludeGroupPosts = true, // Default to excluding group posts
  friendsOnly,
  showSuggested = false
}: PostsContainerProps) => {
  const { toast } = useToast();

  const { data: posts, isLoading, error } = useQuery({
    queryKey,
    queryFn: async () => {
      try {
        let query = supabase
          .from('posts')
          .select(`
            id,
            user_id,
            is_anonymous,
            is_private,
            likes_count,
            created_at,
            updated_at,
            content,
            image_url,
            video_url,
            category,
            custom_tags,
            profiles!posts_user_id_fkey(username, avatar_url),
            comments(
              id,
              content,
              user_id,
              created_at,
              profiles!comments_user_id_fkey(username, avatar_url)
            )
          `);
          
        // Filter out group posts from main feed if requested
        if (excludeGroupPosts) {
          query = query.neq('category', 'group');
        }

        // Handle friend-based feed vs suggested positive content
        if (friendsOnly && friendsOnly.length > 0) {
          // Show posts only from friends
          query = query.in('user_id', friendsOnly);
        } else if (showSuggested) {
          // Use the positive content function for users with no friends
          const { data: positiveData } = await supabase
            .rpc('get_positive_suggested_posts', { limit_count: limit || 20 });
          
          if (positiveData) {
            // Get the profile data for these posts
            const userIds = [...new Set(positiveData.map(p => p.user_id))];
            const { data: profiles } = await supabase
              .from('profiles')
              .select('id, username, avatar_url')
              .in('id', userIds);
            
            // Format the data to match expected Post structure
            return positiveData.map(post => ({
              ...post,
              profiles: profiles?.find(p => p.id === post.user_id),
              comments: [] // We'll load comments separately if needed
            }));
          }
        }

        if (category && category !== 'all') {
          query = query.eq('category', category);
        }

        if (reportedOnly) {
          // First get the reported post IDs
          const { data: reportedPosts, error: reportError } = await supabase
            .from('content_report_counts')
            .select('content_id')
            .eq('content_type', 'post')
            .gt('report_count', 0);

          if (reportError) {
            console.error('Error fetching reported posts:', reportError);
            throw reportError;
          }

          // If there are reported posts, filter by them
          if (reportedPosts && reportedPosts.length > 0) {
            const reportedIds = reportedPosts.map(post => post.content_id);
            query = query.in('id', reportedIds);
          } else {
            // If no reported posts, return empty array
            return [];
          }
        }

        if (orderBy) {
          query = query.order(orderBy.column, { ascending: orderBy.ascending });
        }

        if (limit) {
          query = query.limit(limit);
        }

        const { data, error } = await query;

        if (error) {
          toast({
            title: "Error fetching posts",
            description: error.message,
            variant: "destructive",
          });
          throw error;
        }

        // Transform the fetched data to match the Post interface
        const formattedPosts: Post[] = data?.map(item => {
          if (!item?.id || !item?.user_id) {
            console.error('Invalid post data:', item);
            return null;
          }
          
          // Transform comments data to ensure each comment's profiles is handled properly
          const transformedComments = item.comments?.map(comment => {
            // Extract username and avatar_url safely from comment.profiles
            let commentUsername = '';
            let commentAvatarUrl = '';
            
            // Check if comment.profiles exists and is properly structured
            if (comment.profiles) {
              // Handle if profiles is an array (which seems to be the issue)
              if (Array.isArray(comment.profiles)) {
                if (comment.profiles.length > 0) {
                  const profile = comment.profiles[0] as { username?: string; avatar_url?: string };
                  commentUsername = typeof profile?.username === 'string' 
                    ? profile.username 
                    : '';
                  
                  commentAvatarUrl = typeof profile?.avatar_url === 'string' 
                    ? profile.avatar_url 
                    : '';
                }
              } else {
                // Handle if profiles is an object as expected
                const profile = comment.profiles as { username?: string; avatar_url?: string };
                commentUsername = typeof profile.username === 'string' 
                  ? profile.username 
                  : '';
                
                commentAvatarUrl = typeof profile.avatar_url === 'string' 
                  ? profile.avatar_url 
                  : '';
              }
            }
            
            return {
              id: comment.id,
              content: comment.content,
              user_id: comment.user_id,
              created_at: comment.created_at,
              // Set the profiles with safe values
              profiles: {
                username: commentUsername,
                avatar_url: commentAvatarUrl
              }
            };
          }) || [];
          
          // Handle post profiles safely
          let profileUsername = '';
          let profileAvatarUrl = '';
          
          // Check if item.profiles exists and is properly structured
          if (item.profiles) {
            // Handle if profiles is an array (which seems to be the issue)
            if (Array.isArray(item.profiles)) {
              if (item.profiles.length > 0) {
                const profile = item.profiles[0] as { username?: string; avatar_url?: string };
                profileUsername = typeof profile?.username === 'string' 
                  ? profile.username 
                  : '';
                
                profileAvatarUrl = typeof profile?.avatar_url === 'string' 
                  ? profile.avatar_url 
                  : '';
              }
            } else {
              // Handle if profiles is an object as expected
              const profile = item.profiles as { username?: string; avatar_url?: string };
              profileUsername = typeof profile.username === 'string' 
                ? profile.username 
                : '';
              
              profileAvatarUrl = typeof profile.avatar_url === 'string' 
                ? profile.avatar_url 
                : '';
            }
          }
          
          // Transform data to match the Post interface
          return {
            id: item.id,
            user_id: item.user_id,
            content: item.content,
            image_url: item.image_url,
            video_url: item.video_url,
            category: item.category,
            is_anonymous: item.is_anonymous,
            is_private: item.is_private,
            custom_tags: item.custom_tags,
            likes_count: item.likes_count,
            created_at: item.created_at,
            updated_at: item.updated_at,
            // Set post's profiles with safe values
            profiles: {
              username: profileUsername,
              avatar_url: profileAvatarUrl
            },
            // Use the transformed comments
            comments: transformedComments
          };
        }).filter(Boolean) as Post[];

        return formattedPosts || [];
      } catch (err) {
        console.error('Error in posts query:', err);
        throw err;
      }
    },
    retry: 1,
  });

  if (error) {
    return (
      <div className="text-center py-8 text-red-500">
        Error loading posts. Please try again later.
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[...Array(3)].map((_, i) => (
          <Skeleton key={i} className="h-[200px] w-full" />
        ))}
      </div>
    );
  }

  if (!posts?.length) {
    return (
      <div className="text-center py-8">
        <p className="text-muted-foreground">No posts found</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {posts.map((post: Post) => (
        <PostCard key={post.id} post={post} />
      ))}
    </div>
  );
};
